import type {
  BookingResponse,
  ConversationDetail,
  ConversationListItem,
  GuestProfileResponse,
  MessageItem,
  PropertyResponse,
} from '@/shared/api';

// DEV-ONLY Vorschau-Daten (via ?demo=1). Ermoeglicht, das komplette Inbox-Design befuellt zu sehen,
// ohne echte Channex-Threads. NICHT fuer Produktion — echte Conversations ersetzen das live.
//
// Die Vorschau haelt sich an das, was ueber Channex wirklich ankommt (2026-08-03 gegen Staging und
// die Doku geprueft), damit sie kein Bild verspricht, das der Kanal nicht liefern kann:
//   • `subject` ist der Thread-Titel des Kanals — in aller Regel der **Gastname**, kein Betreff.
//     Sichtbar wird er nur, wenn kein Gastprofil verknuepft ist (Anfrage ohne Buchung).
//   • `sentAt`/`receivedAt` bleiben pro Nachricht leer: die Vendor-Ressource kennt nur einen
//     Zeitstempel. Die Zeitangaben der Konversation leiten wir daraus selbst ab.
//   • `externalMessageId` bleibt leer — es gibt keine OTA-Nachrichtenkennung, dedupet wird ueber
//     die Resource-Id.
// `lastMessagePreview` und `unreadCount` sind dagegen unsere eigenen Werte und bleiben befuellt.

export const DEMO_THREADS: ConversationListItem[] = [
  {
    id: 'demo-1',
    channel: 'BookingCom',
    externalThreadId: 't1',
    propertyId: 'demo-prop',
    propertyName: 'Altbau-Loft Prenzlauer Berg',
    bookingId: 'demo-booking',
    guestProfileId: 'demo-guest',
    guestName: 'Lena Hofmann',
    subject: 'Lena Hofmann',
    isClosed: false,
    lastMessagePreview: 'Super, das ist perfekt. Gibt es WLAN im Apartment?',
    lastActivityAt: '2026-07-17T09:51:00Z',
    lastMessageReceivedAt: '2026-07-17T09:51:00Z',
    lastMessageSentAt: '2026-07-17T09:47:00Z',
    unreadCount: 1,
    otaReservationId: null,
    classification: 'Reservation',
    displayName: 'Lena Hofmann',
    replyOpen: true,
    isLocallyResolved: false,
    awaitingReply: true,
    locallyResolvedAt: null,
    locallyResolvedByUserId: null,
  },
  {
    id: 'demo-2',
    channel: 'Airbnb',
    externalThreadId: 't2',
    propertyId: 'demo-prop',
    propertyName: 'Altbau-Loft Prenzlauer Berg',
    bookingId: null,
    guestProfileId: null,
    guestName: null,
    subject: 'Andrew P.',
    isClosed: false,
    lastMessagePreview: 'Hi! Is the flat available for 2–3 Dec?',
    lastActivityAt: '2026-07-17T08:15:00Z',
    lastMessageReceivedAt: '2026-07-17T08:15:00Z',
    lastMessageSentAt: null,
    unreadCount: 1,
    otaReservationId: null,
    classification: 'Inquiry',
    displayName: 'Andrew P.',
    replyOpen: true,
    isLocallyResolved: false,
    awaitingReply: true,
    locallyResolvedAt: null,
    locallyResolvedByUserId: null,
  },
  {
    id: 'demo-3',
    channel: 'BookingCom',
    externalThreadId: 't3',
    propertyId: 'demo-prop',
    propertyName: 'Altbau-Loft Prenzlauer Berg',
    bookingId: 'demo-booking-3',
    guestProfileId: 'demo-guest',
    guestName: 'Marco Riedel',
    subject: 'Marco Riedel',
    isClosed: true,
    lastMessagePreview: 'Danke, bis morgen dann!',
    lastActivityAt: '2026-07-16T18:25:00Z',
    lastMessageReceivedAt: '2026-07-16T18:20:00Z',
    lastMessageSentAt: '2026-07-16T18:25:00Z',
    unreadCount: 0,
    otaReservationId: null,
    classification: 'Reservation',
    displayName: 'Marco Riedel',
    replyOpen: false,
    isLocallyResolved: false,
    awaitingReply: false,
    locallyResolvedAt: null,
    locallyResolvedByUserId: null,
  },
];

const DETAIL: Record<string, ConversationDetail> = {
  'demo-1': {
    id: 'demo-1',
    channel: 'BookingCom',
    externalThreadId: 't1',
    propertyId: 'demo-prop',
    propertyName: 'Altbau-Loft Prenzlauer Berg',
    bookingId: 'demo-booking',
    guestProfileId: 'demo-guest',
    guestName: 'Lena Hofmann',
    otaReservationId: null,
    subject: 'Lena Hofmann',
    isClosed: false,
    unreadCount: 1,
    canClose: false,
    canMarkNoReplyNeeded: false,
    conversationRevision: 0,
    lastLiveGuestMessageId: null,
    lastReadAt: null,
    lastReadByUserId: null,
    lastActivityAt: '2026-07-17T09:51:00Z',
    lastMessageReceivedAt: '2026-07-17T09:51:00Z',
    lastMessageSentAt: '2026-07-17T09:47:00Z',
    note: 'Früh-Check-in um 12 Uhr zugesagt. Reinigung ist bis 11:30 durch.',
    classification: 'Reservation',
    replyOpen: true,
    isLocallyResolved: false,
    awaitingReply: true,
    locallyResolvedAt: null,
    locallyResolvedByUserId: null,
  },
  'demo-2': {
    id: 'demo-2',
    channel: 'Airbnb',
    externalThreadId: 't2',
    propertyId: 'demo-prop',
    propertyName: 'Altbau-Loft Prenzlauer Berg',
    bookingId: null,
    guestProfileId: null,
    guestName: null,
    otaReservationId: null,
    subject: 'Andrew P.',
    isClosed: false,
    unreadCount: 1,
    canClose: false,
    canMarkNoReplyNeeded: false,
    conversationRevision: 0,
    lastLiveGuestMessageId: null,
    lastReadAt: null,
    lastReadByUserId: null,
    lastActivityAt: '2026-07-17T08:15:00Z',
    lastMessageReceivedAt: '2026-07-17T08:15:00Z',
    lastMessageSentAt: null,
    note: null,
    classification: 'Inquiry',
    replyOpen: true,
    isLocallyResolved: false,
    awaitingReply: true,
    locallyResolvedAt: null,
    locallyResolvedByUserId: null,
  },
  'demo-3': {
    id: 'demo-3',
    channel: 'BookingCom',
    externalThreadId: 't3',
    propertyId: 'demo-prop',
    propertyName: 'Altbau-Loft Prenzlauer Berg',
    bookingId: 'demo-booking-3',
    guestProfileId: 'demo-guest',
    guestName: 'Marco Riedel',
    otaReservationId: null,
    subject: 'Marco Riedel',
    isClosed: true,
    unreadCount: 0,
    canClose: false,
    canMarkNoReplyNeeded: false,
    conversationRevision: 0,
    lastLiveGuestMessageId: null,
    lastReadAt: null,
    lastReadByUserId: null,
    lastActivityAt: '2026-07-16T18:25:00Z',
    lastMessageReceivedAt: '2026-07-16T18:20:00Z',
    lastMessageSentAt: '2026-07-16T18:25:00Z',
    note: null,
    classification: 'Reservation',
    replyOpen: false,
    isLocallyResolved: false,
    awaitingReply: false,
    locallyResolvedAt: null,
    locallyResolvedByUserId: null,
  },
};

function msg(over: Partial<MessageItem> & Pick<MessageItem, 'id' | 'sender'>): MessageItem {
  return {
    externalMessageId: null,
    body: null,
    attachments: [],
    metaJson: null,
    occurredAt: over.occurredAt ?? '2026-07-17T09:00:00Z',
    sentAt: null,
    receivedAt: null,
    sendStatus: 'Sent',
    sendError: null,
    composedBy: null,
    ...over,
  };
}

const MESSAGES: Record<string, MessageItem[]> = {
  'demo-1': [
    msg({
      id: 'm1',
      sender: 'Guest',
      body: 'Guten Morgen! Wir kommen morgen an — wäre ein früher Check-in um 12 Uhr möglich? Unser Zug ist schon um 11 da. 🙂',
      occurredAt: '2026-07-17T09:42:00Z',
    }),
    msg({
      id: 'm2',
      sender: 'Host',
      body: 'Hallo Lena, guten Morgen! Ein Check-in um 12 Uhr geht klar — das Apartment ist ab 11:30 frei. Ich hinterlege Ihnen den Türcode rechtzeitig im Gastportal.',
      occurredAt: '2026-07-17T09:47:00Z',
    }),
    msg({
      id: 'm3',
      sender: 'Guest',
      body: 'Super, das ist perfekt. Gibt es WLAN im Apartment?',
      occurredAt: '2026-07-17T09:51:00Z',
    }),
  ],
  'demo-2': [
    msg({
      id: 'm4',
      sender: 'System',
      body: 'Anfrage: 02.–03. Dez · 3 Gäste · 50,00 GBP',
      metaJson:
        '{"booking_details":{"checkin":"2026-12-02","checkout":"2026-12-03","nights":1,"guests":3,"currency":"GBP"}}',
      occurredAt: '2026-07-17T08:14:00Z',
    }),
    msg({
      id: 'm5',
      sender: 'Guest',
      body: 'Hi! Is the flat available for 2–3 Dec?',
      occurredAt: '2026-07-17T08:15:00Z',
    }),
  ],
  'demo-3': [
    msg({
      id: 'm6',
      sender: 'Guest',
      body: 'Danke, bis morgen dann!',
      occurredAt: '2026-07-16T18:20:00Z',
    }),
  ],
};

const DEMO_PROPERTY: PropertyResponse = {
  id: 'demo-prop',
  tenantId: 't',
  name: 'Altbau-Loft Prenzlauer Berg',
  slug: 'altbau-loft',
  propertyType: 'Apartment',
  timezone: 'Europe/Berlin',
  address: {
    street: 'Kastanienallee 47',
    streetName: 'Kastanienallee',
    houseNumber: '47',
    city: 'Berlin',
    postalCode: '10435',
    countryCode: 'DE',
  },
  occupancyAdults: 3,
  occupancyChildren: 0,
  bedrooms: 1,
  bathrooms: 1,
  availableBedCount: 1,
  availableSofaBedCount: 0,
  checkInTime: '14:00:00',
  checkOutTime: '11:00:00',
  currency: 'EUR',
  cleaningFee: 60,
  baseNightlyRate: 184,
  status: 'Active',
  isActive: true,
  channexUnitPublished: false,
  createdAt: '',
  wiFiSsid: 'StayOn-Loft-2F',
  hasWiFiPassword: true,
  emergencyContact: '+49 30 5544 1122',
};

const DEMO_BOOKING: BookingResponse = {
  id: 'demo-booking',
  bookingReference: 'SO-9M4XR7',
  tenantId: 't',
  propertyId: 'demo-prop',
  guestProfileId: 'demo-guest',
  checkInDate: '2026-07-18',
  checkOutDate: '2026-07-21',
  status: 'Confirmed',
  guestCount: 2,
  adults: 2,
  children: 0,
  infants: 0,
  totalPrice: 642,
  baseRate: 184,
  cleaningFee: 60,
  currency: 'EUR',
  source: 'Ota',
  createdAt: '',
  arrivalHour: '12:00:00',
};

const DEMO_GUEST: GuestProfileResponse = {
  id: 'demo-guest',
  bookingId: 'demo-booking',
  firstName: 'Lena',
  lastName: 'Hofmann',
  email: 'lena.hofmann@posteo.de',
  phone: '+49 151 2345 6789',
  status: 'Final',
  bookingType: 'B2C',
  dateOfBirth: '1991-03-14',
  nationality: 'Deutschland',
  identityKind: null,
  identityNumber: null,
  identityIssuingCountry: null,
  streetAddress: 'Lindenstr. 12',
  city: 'München',
  postalCode: '80331',
  country: 'Deutschland',
  foreignCompanionCount: 0,
  foreignCompanionNationalities: [],
  onboardingCompletedAt: '2026-07-10T10:00:00Z',
  billing: {
    firstName: null,
    lastName: null,
    street: null,
    zip: null,
    city: null,
    country: null,
    phone: null,
    email: null,
  },
  company: null,
  rentalAgreementAcceptedAt: null,
  preferredLocale: 'de',
  documents: [],
  consents: [],
};

export const demoInbox = {
  threads: DEMO_THREADS,
  detail: (id: string | null): ConversationDetail | undefined => (id ? DETAIL[id] : undefined),
  messages: (id: string | null): MessageItem[] => (id ? (MESSAGES[id] ?? []) : []),
  context: (id: string | null) =>
    id === 'demo-1'
      ? { booking: DEMO_BOOKING, property: DEMO_PROPERTY, guest: DEMO_GUEST }
      : id === 'demo-2'
        ? { booking: null, property: DEMO_PROPERTY, guest: null }
        : undefined,
};
